import React, { useContext, useEffect, useState } from "react";
import { ChatContext } from "src/scenes/Chat/ChatContext";

import {
    FlexContainer,
    Toast,
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalHelperText,
    StandardModalInfoCard
} from "src/components";
import { FORM_TYPE_EDIT, USER_TYPE_EMPLOYEES, USER_TYPE_CLIENTS } from "src/constants/chat";
import Group from "src/scenes/Chat/components/create/Group";
import LeadershipRoleToUseSelection from "src/scenes/Chat/components/create/LeadershipRoleToUseSelection";
import SelectUsersSection from "./SelectUsersSection";
import { GridContainer } from "./CreateChatModal";
import { useFetchUsersList, useLeaveChat } from "src/scenes/Chat/hooks";
import GroupAdminSelectionModal from "./GroupAdminSelectionModal";

const EditGroupChatModal = () => {
    const {
        useChatMessage: ctxUseChatMessage,
        useEditChat: ctxUseEditChat,
        useDeleteChat: ctxUseDeleteChat
    } = useContext(ChatContext);

    const {
        isDeletingChat,
        handleShowDeleteConfirmationModal
    } = ctxUseDeleteChat;

    const { openedMatrixChat } = ctxUseChatMessage;
    const { setShowEditGroupChatModal, updateGroupChat } = ctxUseEditChat;

    const showDialog = true;
    const handleClose = () => setShowEditGroupChatModal(false);
    const [selectedUserListType, setSelectedUserListType] = useState(USER_TYPE_EMPLOYEES);
    const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
    const [isSelectingNewAdmin, setIsSelectingNewAdmin] = useState(false);
    const [newOwnerEmployeeUuid, setNewOwnerEmployeeUuid] = useState("");
    const { isLeavingChat, leaveChat } = useLeaveChat();

    const [formData, setFormData] = useState({
        allowUsersToLeave: openedMatrixChat?.allowUsersToLeave || false,
        isEdit: FORM_TYPE_EDIT,
        groupChatName: openedMatrixChat?.name || "",
        leadershipRoleUuid: openedMatrixChat?.leadershipRoleUuid || undefined,
        selectedUsersByType: {
            [USER_TYPE_EMPLOYEES]: openedMatrixChat?.users
                ?.filter(u => !u.employeeDetails?.isClient && u.currentMember)
                .map(u => u.employeeUuid) || [],
            [USER_TYPE_CLIENTS]: openedMatrixChat?.users
                ?.filter(u => u.employeeDetails?.isClient && u.currentMember) 
                .map(u => u.employeeUuid) || []
        }
    });

    const [usersByType, setUsersByType] = useState({
        [USER_TYPE_EMPLOYEES]: [],
        [USER_TYPE_CLIENTS]: []
    });

    const [searchFilteredUsersByType, setSearchFilteredUsersByType] = useState({
        [USER_TYPE_EMPLOYEES]: [],
        [USER_TYPE_CLIENTS]: []
    });
    

    const [loadingFetchUsers, setLoadingFetchUsers] = useState(false);
    const [isUserListBeingSearched, setIsUserListBeingSearched] = useState(false);
    const { fetchUsersList } = useFetchUsersList();

    const retrieveUpdatedUsersList = async (type) => {
        setLoadingFetchUsers(true);
        let users = usersByType[type] || [];
        if (!users.length) {
            users = await fetchUsersList(type);
        }
        const validUsers = users.filter(u => !!u.matrixUserId);
        setUsersByType(prev => ({ ...prev, [type]: validUsers }));
        setSearchFilteredUsersByType(prev => ({ ...prev, [type]: validUsers }));
        setLoadingFetchUsers(false);
    };

    useEffect(() => {
        retrieveUpdatedUsersList(USER_TYPE_EMPLOYEES);
        retrieveUpdatedUsersList(USER_TYPE_CLIENTS);
    }, [openedMatrixChat]);

    const handleSearch = (searchKey) => {
        const currentList = usersByType[selectedUserListType] || [];

        if (searchKey.length) {
            const filteredUsers = currentList.filter(user => 
                user.firstName.toLowerCase().includes(searchKey.toLowerCase())
            || user.lastName.toLowerCase().includes(searchKey.toLowerCase())    
            );
            setIsUserListBeingSearched(true);
            setSearchFilteredUsersByType(prev => ({ ...prev, [selectedUserListType]: filteredUsers }));
        } else {
            setIsUserListBeingSearched(false);
            setSearchFilteredUsersByType(prev => ({ ...prev, [selectedUserListType]: currentList }));
        }
    };
    
    const changeSelectedType = (type) => {
        if (type === selectedUserListType) return;
        setSelectedUserListType(type);
        retrieveUpdatedUsersList(type);
    };

    const handleSelectedUsersToChat = (data, type) => {
        setFormData(prev => ({
            ...prev,
            selectedUsersByType: { ...prev.selectedUsersByType, [type]: data }
        }));
    };

    const handleSetGroupChatName = (name) => setFormData(prev => ({ ...prev, groupChatName: name }));

    const handleSubmit = () => {
        const clientUuids = formData.selectedUsersByType[USER_TYPE_CLIENTS] || [];
        const allUuids = [
            ...formData.selectedUsersByType[USER_TYPE_EMPLOYEES],
            ...formData.selectedUsersByType[USER_TYPE_CLIENTS]
        ].filter(Boolean);

        if (!allUuids.length) {
            Toast.error("Please select at least one member.");
            return;
        }

        const payload = {
            allowUsersToLeave: formData.allowUsersToLeave,
            employeeUuidsToChat: Array.from(new Set(allUuids)),
            leadershipRoleUuid: formData.leadershipRoleUuid ?? "",
            groupChatName: formData.groupChatName || "",
            isClient: clientUuids.length > 0,
        };

        updateGroupChat(openedMatrixChat.uuid, payload);
    };

    const checkLeadershipRoleVisibility = (currentSelectedLeadershipUuid, checkerType, leadershipRoles) => {
        if (currentSelectedLeadershipUuid === undefined || currentSelectedLeadershipUuid === "") {
            return true;
        }
    
        const selectedLeadershipRole = leadershipRoles.find((leadershipRole) => leadershipRole.uuid === currentSelectedLeadershipUuid);
        if (selectedLeadershipRole && currentSelectedLeadershipUuid === selectedLeadershipRole.uuid) {
            if (checkerType === USER_TYPE_EMPLOYEES) {
                return selectedLeadershipRole.isVisibleToEmployees;
            } else if (checkerType === USER_TYPE_CLIENTS) {
                return selectedLeadershipRole.isVisibleToClients;
            }
        }
    };

    const pendingRemovalCount = openedMatrixChat?.users?.filter(user =>
        user.currentMember && user.requestingLeave
    ).length || 0;
    const eligibleNewOwnerCount = openedMatrixChat?.users?.filter(user =>
        user.currentMember && user.employeeUuid !== openedMatrixChat.ownerUuid && user.couldOwnThisChat
    ).length || 0;

    const handleStartAdminSelection = () => {
        setShowLeaveGroupModal(false);
        setIsSelectingNewAdmin(true);
        setNewOwnerEmployeeUuid("");
    };

    const handleCancelAdminSelection = () => {
        setIsSelectingNewAdmin(false);
        setNewOwnerEmployeeUuid("");
    };

    const handleLeaveGroup = async () => {
        if (!newOwnerEmployeeUuid) {
            return;
        }
        await leaveChat(openedMatrixChat, newOwnerEmployeeUuid);
        setIsSelectingNewAdmin(false);
        setShowLeaveGroupModal(false);
        handleClose();
    };
        
    return (
        <>
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                title="Edit Group Chat"
                widthMode="compactWide"
                footer={!loadingFetchUsers && !isDeletingChat && (
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem" }}>
                        {isSelectingNewAdmin ? (
                            <>
                                <StandardModalActionButton
                                    $variant="primary"
                                    disabled={!newOwnerEmployeeUuid || isLeavingChat}
                                    onClick={handleLeaveGroup}
                                >
                                    Confirm Assign
                                </StandardModalActionButton>
                                <StandardModalActionGroup $pushRight>
                                    <StandardModalActionButton onClick={handleCancelAdminSelection}>
                                        Cancel Selection
                                    </StandardModalActionButton>
                                </StandardModalActionGroup>
                            </>
                        ) : (
                            <>
                                <StandardModalActionGroup>
                                    <StandardModalActionButton onClick={() => setShowLeaveGroupModal(true)}>
                                        Leave Group
                                    </StandardModalActionButton>
                                    <StandardModalActionButton
                                        $variant="danger"
                                        onClick={() => handleShowDeleteConfirmationModal(openedMatrixChat)}
                                    >
                                        Delete Group
                                    </StandardModalActionButton>
                                </StandardModalActionGroup>
                                <StandardModalActionGroup $pushRight>
                                    <StandardModalActionButton onClick={handleClose}>Cancel</StandardModalActionButton>
                                    <StandardModalActionButton $variant="primary" onClick={handleSubmit}>
                                        Save Changes
                                    </StandardModalActionButton>
                                </StandardModalActionGroup>
                            </>
                        )}
                    </div>
                )}
            >
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <FlexContainer style={{ height: "100%" }}>
                        {loadingFetchUsers || isDeletingChat ? (
                            <LottieLoadingIndicator />
                        ) : (
                            <>
                                {pendingRemovalCount > 0 && (
                                    <StandardModalInfoCard>
                                        <StandardModalHelperText>
                                            {pendingRemovalCount} {pendingRemovalCount === 1 ? "member has" : "members have"}
                                            {" requested to be removed from this group."}
                                        </StandardModalHelperText>
                                    </StandardModalInfoCard>
                                )}
                                {isSelectingNewAdmin && (
                                    <StandardModalInfoCard>
                                        <StandardModalHelperText>
                                            {eligibleNewOwnerCount > 0
                                                ? "Select a group member with Full Chat Access to become the new admin."
                                                : "No group members have Full Chat Access. You cannot leave until an eligible admin can be assigned."}
                                        </StandardModalHelperText>
                                    </StandardModalInfoCard>
                                )}
                                <GridContainer>
                                    <LeadershipRoleToUseSelection
                                        formData={formData}
                                        setFormData={setFormData}
                                        checkLeadershipRoleVisibility={checkLeadershipRoleVisibility}
                                        changeSelectedType={changeSelectedType}
                                    />
                                </GridContainer>

                                <SelectUsersSection
                                    loadingFetchUsers={loadingFetchUsers}
                                    usersList={usersByType[selectedUserListType] || []}
                                    isUserListBeingSearched={isUserListBeingSearched}
                                    handleClose={handleClose}
                                    handleSubmit={handleSubmit}
                                    currentUserType={selectedUserListType}
                                    handleShowDeleteConfirmationModal={handleShowDeleteConfirmationModal}
                                    openedMatrixChat={openedMatrixChat}
                                    isEdit={true}
                                    hideFooter={true}
                                >
                                    <Group
                                        availableUsersList={[
                                            ...usersByType[USER_TYPE_EMPLOYEES],
                                            ...usersByType[USER_TYPE_CLIENTS]
                                        ]}
                                        searchFilteredUserList={
                                            searchFilteredUsersByType[selectedUserListType] || []
                                        }
                                        selectedUserListType={selectedUserListType}
                                        formType={FORM_TYPE_EDIT}
                                        handleSearch={handleSearch}
                                        handleSelectedEmployeesToChat={(data) =>
                                            handleSelectedUsersToChat(data, selectedUserListType)
                                        }
                                        handleSetGroupChatName={handleSetGroupChatName}
                                        formData={formData}
                                        setFormData={setFormData}
                                        isSelectingNewAdmin={isSelectingNewAdmin}
                                        selectedNewAdminUuid={newOwnerEmployeeUuid}
                                        onSelectNewAdmin={setNewOwnerEmployeeUuid}
                                    />
                                </SelectUsersSection>
                            </>
                        )}
                    </FlexContainer>
                </div>
            </StandardModal>
            <GroupAdminSelectionModal
                open={showLeaveGroupModal}
                onClose={() => setShowLeaveGroupModal(false)}
                onDelete={() => handleShowDeleteConfirmationModal(openedMatrixChat)}
                onSelect={handleStartAdminSelection}
            />
        </>
    );
};

export default EditGroupChatModal;
